import { useState } from 'react';
import {
  apiErrorText,
  useAcceptTenderSuggestionMutation,
  useAnnotateTenderIssueMutation,
} from '@shared/api';
import type { TenderIssue } from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { useAgentPermission } from '@entities/user';
import { decisionLabel } from '@shared/lib/smetaLabels';
import { DecisionBtns, Muted, MutedMargin, Table } from './styled';
import { issueKindLabel, issueTone, suggestionText } from './tenderLabels';

interface IssuesTableProps {
  items: TenderIssue[];
  estimateId: string;
}

/** Таблица найденных ошибок исходных данных + HITL-разметка. */
export function IssuesTable({ items, estimateId }: IssuesTableProps) {
  const [annotate, { isLoading }] = useAnnotateTenderIssueMutation();
  const [accept, { isLoading: accepting }] = useAcceptTenderSuggestionMutation();
  const [applyErr, setApplyErr] = useState<string | null>(null);
  const { can_write } = useAgentPermission('smeta');
  if (items.length === 0) {
    return <MutedMargin>Ошибок исходных данных не выявлено.</MutedMargin>;
  }

  async function applySuggestion(issueId: string, itemId: string) {
    setApplyErr(null);
    try {
      await accept({ estimateId, itemId, issueId }).unwrap();
    } catch (e) {
      setApplyErr(apiErrorText(e, 'Не удалось применить шифр'));
    }
  }

  return (
    <Table>
      <thead>
        <tr>
          <th>Важность</th>
          <th>Вид</th>
          <th>Сообщение</th>
          <th>Предложение (авто-fix)</th>
          <th>Решение</th>
        </tr>
      </thead>
      <tbody>
        {items.map((d) => (
          <tr key={d.id}>
            <td>
              <Badge $tone={issueTone(d.severity)}>{d.severity}</Badge>
            </td>
            <td>{issueKindLabel(d.kind)}</td>
            <td>{d.message}</td>
            <td>
              {suggestionText(d)}
              {d.auto_fixed && <Muted>исправлено автоматически</Muted>}
              {/* Кнопка живёт здесь, а не в блоке решения: она меняет РАСЧЁТ,
                  а «Принять/Спорно/Отклонить» рядом — только пометка. Раньше
                  сметчик жал «Принять» и был уверен, что подсказка применена,
                  хотя шифр и сумма оставались прежними. Показываем её и при
                  уже проставленном решении — по той же причине. */}
              {d.suggested_code && d.item_id && can_write && (
                <Button
                  $variant="ghost"
                  $size="sm"
                  type="button"
                  disabled={accepting}
                  title="Записать этот шифр в позицию и пересчитать её"
                  onClick={() => void applySuggestion(d.id, d.item_id as string)}
                >
                  Применить шифр
                </Button>
              )}
              {applyErr && <Muted>{applyErr}</Muted>}
            </td>
            <td>
              {d.decision ? (
                <Badge
                  $tone={
                    d.decision === 'accepted'
                      ? 'success'
                      : d.decision === 'rejected'
                        ? 'neutral'
                        : 'warning'
                  }
                >
                  {decisionLabel(d.decision)}
                </Badge>
              ) : (
                <DecisionBtns>
                  <Button
                    $variant="ghost"
                    $size="sm"
                    type="button"
                    disabled={isLoading || !can_write}
                    title={!can_write ? 'Только чтение' : undefined}
                    onClick={() => annotate({ id: d.id, estimateId, decision: 'accepted' })}
                  >
                    Принять
                  </Button>
                  <Button
                    $variant="ghost"
                    $size="sm"
                    type="button"
                    disabled={isLoading || !can_write}
                    title={!can_write ? 'Только чтение' : undefined}
                    onClick={() => annotate({ id: d.id, estimateId, decision: 'disputed' })}
                  >
                    Спорно
                  </Button>
                  <Button
                    $variant="ghost"
                    $size="sm"
                    type="button"
                    disabled={isLoading || !can_write}
                    title={!can_write ? 'Только чтение' : undefined}
                    onClick={() => annotate({ id: d.id, estimateId, decision: 'rejected' })}
                  >
                    Отклонить
                  </Button>
                </DecisionBtns>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
